import apiClient from "../apiClient";
import { CommonApiResponse } from "../../Controllers/Types/ApiResponse";
import { baseURL } from "../ApiConstant";
import { FeatureOption, FSMainFeature, FSProject } from "../../Controllers/Types/FinishingSchedule/FinishingSchedule";
import { ShareContactType } from "../../Controllers/Types/Share/ShareContactType";

// get project data
export const getFSProjectData = async () => {
    try {
        const response = await apiClient.get(`/api/fs-project-data`);

        if (response.status === 200) {
            return { status: true, body: response.data };
        } else {
            return { status: false, error: "failed" };
        }
    } catch (error: any) {
        console.error("Error fetching data:", error);

        return {
            status: false,
            error: "An error occurred. Please try again later.",
        };
    }
};

// Add projects
export const addProjectForFinishingSchedule = async (
    projectList: FSProject[]
): Promise<CommonApiResponse> => {
    try {
        // prepare ids for projects
        const list = projectList.map((e) => e.project_id);

        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;

        const response = await fetch(`${baseURL}/api/fs-project-add`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: FULL_TOKEN,
            },
            body: JSON.stringify({ list }),
        });

        const data = await response.json();

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return {
                status: false,
                statusCode: 422,
                errors: data["errors"],
                massage: "Body error",
            };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Delete file
export const deleteFSFile = async (
    deleteFrom: 'Light' | 'Door' | 'Paint' | 'Bathroom' | 'Kitchen' | 'Tiles' | 'Floor Finishing' | 'Others',
    isAccessoryData: boolean,
    dataId: number,
    fileIndex: number
): Promise<CommonApiResponse> => {
    try {
        let apiUrl = '';
        let dataIdField = '';

        if (isAccessoryData) {
            apiUrl = 'fs-paint-accessory-file';
            dataIdField = 'paint_accessory_data_id';
        } else {
            switch (deleteFrom) {
                case 'Light':
                    apiUrl = 'fs-light-file';
                    dataIdField = 'light_data_id';
                    break;
                case 'Door':
                    apiUrl = 'fs-door-file';
                    dataIdField = 'door_data_id';
                    break;
                case 'Paint':
                    apiUrl = 'fs-paint-file';
                    dataIdField = 'paint_data_id';
                    break;
                case 'Bathroom':
                    apiUrl = 'fs-bathroom-file';
                    dataIdField = 'bathroom_data_id';
                    break;
                case 'Kitchen':
                    apiUrl = 'fs-kitchen-data-file';
                    dataIdField = 'kitchen_data_id';
                    break;
                case 'Tiles':
                    apiUrl = 'fs-tile-file';
                    dataIdField = 'tile_data_id';
                    break;
                case 'Floor Finishing':
                    apiUrl = 'fs-floor-finishing-file';
                    dataIdField = 'floor_finishing_data_id';
                    break;
                case 'Others':
                    apiUrl = 'fs-other-file';
                    dataIdField = 'other_data_id';
                    break;
            }
        }

        const token = localStorage.getItem('token');
        const FULL_TOKEN = `Bearer ${token}`;

        const body = {
            [dataIdField]: dataId,
            index: fileIndex
        };

        const response = await fetch(`${baseURL}/api/${apiUrl}/delete`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                Authorization: FULL_TOKEN,
            },
            body: JSON.stringify(body),
        });

        const data = await response.json();

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return {
                status: false,
                statusCode: 422,
                errors: data["errors"],
                massage: "Body error",
            };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// get finishing schedule main feature data
export const getFSMainFeatureList = async () => {
    try {
        const response = await apiClient.get(`/api/fs-main-feature-list`);

        if (response.status === 200) {
            return { status: true, body: response.data };
        } else {
            return { status: false, error: "failed" };
        }
    } catch (error: any) {
        console.error("Error fetching data:", error);

        return {
            status: false,
            error: "An error occurred. Please try again later.",
        };
    }
};

// Add finishing schedule features
export const addFSFeatures = async (
    project_id: number,
    featuresList: FSMainFeature[]
): Promise<CommonApiResponse> => {
    try {
        // prepare ids for features
        const main_feature_list = featuresList.map((e) => e.id);

        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;

        const response = await fetch(`${baseURL}/api/fs-project-main-feature-add`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: FULL_TOKEN,
            },
            body: JSON.stringify({ project_id, main_feature_list }),
        });

        const data = await response.json();

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return {
                status: false,
                statusCode: 422,
                errors: data["errors"],
                massage: "Body error",
            };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Delete finishing schedule feature
export const deleteFSFeature = async (
    id: number
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;

        const response = await fetch(
            `${baseURL}/api/fs-project-main-feature-delete/${id}`,
            {
                method: "DELETE",
                headers: {
                    Accept: "application/json",
                    Authorization: FULL_TOKEN,
                },
            }
        );

        const data = await response.json();

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return {
                status: false,
                statusCode: 422,
                errors: data["errors"],
                massage: "Body error",
            };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Finishing schedule order create/update
export const fsOrderProcess = async (
    category: 'Light' | 'Door' | 'Paint' | 'Bathroom' | 'Kitchen' | 'Tiles' | 'Floor Finishing' | 'Others',
    is_accessory_data: 1 | 0,
    status: 'Pending' | 'Ordered' | 'Instore' | 'Onsite',
    order_add_id: number,
    lead_time_week: number | null,
    lead_time_day: number | null,
    date: string | null,
    is_price_include: 1 | 0,
    price: number | null,
    is_include_vat: 1 | 0,
    note: string | null
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;
        const formData = new FormData();

        formData.append("category", category);
        formData.append("is_accessory_data", String(is_accessory_data));
        formData.append("status", status);
        formData.append("order_add_id", String(order_add_id));

        if (lead_time_week !== null) {
            formData.append("lead_time_week", String(lead_time_week));
        }

        if (lead_time_day !== null) {
            formData.append("lead_time_day", String(lead_time_day));
        }

        if (date !== null) {
            formData.append("date", date);
        }

        formData.append("is_price_include", String(is_price_include));

        if (price !== null) {
            formData.append("price", String(price));
        }

        formData.append("is_include_vat", String(is_include_vat));

        if (note !== null) {
            formData.append("note", note);
        }

        const response = await fetch(`${baseURL}/api/fs-order/add-update`, {
            method: "POST",
            headers: {
                Accept: "application/json",
                Authorization: FULL_TOKEN,
            },
            body: formData,
        });

        const data = await response.json();

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return {
                status: false,
                statusCode: 422,
                errors: data["errors"],
                massage: "Body error",
            };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Share feature
export const shareFSFeature = async (
    fs_feature_id: number,
    share: ShareContactType[]
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;
        const response = await fetch(
            `${baseURL}/api/fs-feature-share`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    Authorization: FULL_TOKEN,
                },
                body: JSON.stringify({ fs_feature_id, share }),
            }
        );

        const data = await response.json();

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return {
                status: false,
                statusCode: 422,
                errors: data["errors"],
                massage: "Body error",
            };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};

// Finishing schedule report preview
export const getFSReportData = async (
    generateReport: 1 | 0,
    featureOptions: FeatureOption[]
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;

        const formData = new FormData();

        formData.append("generate_report", String(generateReport));

        featureOptions.forEach((item, index) => {
            formData.append(`feature_list[${index}][id]`, item.featureId.toString());
            formData.append(`feature_list[${index}][show_accessory_data]`, item.accessoryView.toString());
        });

        const response = await fetch(`${baseURL}/api/fs-report`, {
            method: "POST",
            headers: {
                Accept: "application/json",
                Authorization: FULL_TOKEN,
            },
            body: formData,
        });

        const data = await response.json();

        if (response.status === 200) {
            return {
                status: true,
                statusCode: 200,
                data,
            };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return {
                status: false,
                statusCode: 422,
                errors: data["errors"],
                massage: "Body error",
            };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Server error" };
        }
    } catch (error) {
        throw error;
    }
};

// Send report via email
export const sendFSReportViaEmail = async (
    project_id: number,
    fs_report_download_path: string,
    email_list: string[]
): Promise<CommonApiResponse> => {
    try {
        const token = localStorage.getItem("token");
        const FULL_TOKEN = `Bearer ${token}`;
        const response = await fetch(`${baseURL}/api/fs-report-email-send`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: FULL_TOKEN,
            },
            body: JSON.stringify({
                project_id,
                fs_report_download_path,
                ...(email_list && email_list.length > 0 ? { email_list } : {})
            }),
        });

        const data = await response.json();

        if (response.status === 200) {
            return { status: true, statusCode: 200 };
        } else if (response.status === 401) {
            return { status: false, statusCode: 401, massage: "Unauthorized" };
        } else if (response.status === 422) {
            return {
                status: false,
                statusCode: 422,
                errors: data["errors"],
                massage: "Body error",
            };
        } else if (response.status === 404) {
            return { status: false, statusCode: 404, massage: "Not found" };
        } else {
            return { status: false, statusCode: 500, massage: "Error" };
        }
    } catch (error) {
        throw error;
    }
};